import { Head, router } from "@inertiajs/react"
import { SearchLg } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import { PageHeader } from "@/components/console/page-header"
import type { StatusTier } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminUserPath, adminUsersPath } from "@/routes"

import type { Role, SignIn, UserRow } from "./types"

interface Props {
  users: UserRow[]
  roles: { key: Role; label: string; count: number }[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
  filters: { q: string | null; role: Role }
}

/** How each person signs in, as a status tier: an identity is settled, a password is plain, nothing yet needs a person. */
const SIGN_IN: Record<SignIn, { tier: StatusTier; label: string }> = {
  salesforce: { tier: "confirmed", label: "Salesforce" },
  password: { tier: "neutral", label: "Password" },
  awaiting_sign_in: { tier: "caution", label: "Awaiting sign-in" },
}

/**
 * Every account, for the developers: how they sign in, what they own and what
 * they have done. The row opens the account, where browsing as them lives.
 */
export default function AdminUsersIndex({
  users,
  roles,
  pagination,
  filters,
}: Props) {
  const [query, setQuery] = useState(filters.q ?? "")

  const visit = (next: { q?: string | null; role?: Role; page?: number }) => {
    const q = next.q === undefined ? filters.q : next.q
    const role = next.role ?? filters.role
    router.get(
      adminUsersPath(),
      {
        ...(q ? { q } : {}),
        ...(role !== "all" ? { role } : {}),
        ...(next.page && next.page > 1 ? { page: next.page } : {}),
      },
      { preserveState: true, replace: true },
    )
  }

  // The search fires once typing pauses, and never on mount.
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    const t = setTimeout(() => visit({ q: query || null, page: 1 }), 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const roleLabel =
    roles.find((r) => r.key === filters.role)?.label ?? filters.role
  const tokens = filters.q
    ? [{ label: `search: ${filters.q}`, onClear: () => setQuery("") }]
    : []

  return (
    <AdminLayout>
      <Head title="Users" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "access" }, { label: "users" }]}
          title="Users"
          description="Every account: how each person signs in, the turfs they own and the calls they have made. Open one to browse the console as them."
        />

        <Tabs
          selectedKey={filters.role}
          onSelectionChange={(k) => visit({ role: String(k) as Role, page: 1 })}
        >
          <TabList
            size="sm"
            type="underline"
            items={roles.map((r) => ({
              id: r.key,
              label: r.label,
              badge: r.count,
            }))}
          />
        </Tabs>

        <TableCard.Root bleed>
          <TableCard.Caption
            title={roleLabel}
            count={`${pagination.total} ${pagination.total === 1 ? "account" : "accounts"}`}
            tokens={tokens}
            note="name ↑"
            trailing={
              <Input
                size="sm"
                icon={SearchLg}
                aria-label="Search users"
                placeholder="Name or email"
                value={query}
                onChange={setQuery}
                className="w-56"
              />
            }
          />
          {users.length === 0 ? (
            <TableCard.Empty>
              {filters.q
                ? "Nobody matches that search."
                : "No accounts here yet."}
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Users">
                <Table.Header>
                  <Table.Head
                    id="user"
                    isRowHeader
                    label="User"
                    className="w-full"
                  />
                  <Table.Head id="sign_in" label="Sign-in" />
                  <Table.Head id="access" label="Access" />
                  <Table.Head id="turfs" label="Turfs" />
                  <Table.Head id="calls" label="Calls" align="right" />
                  <Table.Head id="last_seen" label="Last seen" align="right" />
                  <Table.Head id="joined" label="Joined" align="right" />
                </Table.Header>
                <Table.Body items={users}>
                  {(row) => (
                    <Table.Row id={row.id} href={adminUserPath(row.id)}>
                      <Table.Lead sub={row.email}>{row.name}</Table.Lead>
                      <Table.Status tier={SIGN_IN[row.sign_in].tier}>
                        {SIGN_IN[row.sign_in].label}
                      </Table.Status>
                      <Table.Status
                        tier={row.super_admin ? "remote" : "neutral"}
                      >
                        {row.super_admin
                          ? "Super admin"
                          : row.staff
                            ? "Staff"
                            : null}
                      </Table.Status>
                      <Table.Cell>
                        {row.turfs.length > 0 ? row.turfs.join(" · ") : "—"}
                      </Table.Cell>
                      <Table.Figure>{row.calls_count || null}</Table.Figure>
                      <Table.When
                        at={row.last_seen_at}
                        pattern="d MMM yyyy"
                        relative
                      />
                      <Table.When at={row.created_at} pattern="d MMM yyyy" />
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer
                shown={users.length}
                total={pagination.total}
                filters
                trailing={
                  pagination.total_pages > 1 ? (
                    <PaginationCardMinimal
                      align="right"
                      page={pagination.page}
                      total={pagination.total_pages}
                      onPageChange={(p) => visit({ page: p })}
                      className="border-0 p-0 md:p-0"
                    />
                  ) : undefined
                }
              />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
